import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LookupService, ServiceCompanyAdmin } from '../../services/lookup.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/confirm-dialog/confirm-dialog.component';
import { getApiErrorMessage } from '../../shared/api-error-message';

@Component({
  selector: 'app-manage-service-companies',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatProgressSpinnerModule],
  templateUrl: './manage-service-companies.component.html',
  styleUrl: './manage-service-companies.component.css'
})
export class ManageServiceCompaniesComponent implements OnInit {
  companies: ServiceCompanyAdmin[] = [];
  newName = '';
  editingId: number | null = null;
  editName = '';
  isLoading = false;
  isSaving = false;
  errorMessage = '';

  constructor(
    private readonly lookupService: LookupService,
    private readonly dialog: MatDialog,
    public readonly dialogRef: MatDialogRef<ManageServiceCompaniesComponent>
  ) {}

  ngOnInit(): void {
    this.loadCompanies();
  }

  loadCompanies(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.lookupService.getAllServiceCompanies().subscribe({
      next: companies => {
        this.companies = this.sortCompanies(companies);
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.errorMessage = getApiErrorMessage(error, 'Unable to load service companies.');
      }
    });
  }

  addCompany(): void {
    const name = this.validateName(this.newName);
    if (!name) return;

    this.isSaving = true;
    this.errorMessage = '';
    this.lookupService.createServiceCompany({ name }).subscribe({
      next: company => {
        this.companies = this.sortCompanies([...this.companies, company]);
        this.newName = '';
        this.isSaving = false;
      },
      error: error => {
        this.isSaving = false;
        this.errorMessage = getApiErrorMessage(error, 'Unable to create the service company.');
      }
    });
  }

  startEdit(company: ServiceCompanyAdmin): void {
    this.editingId = company.id;
    this.editName = company.name;
    this.errorMessage = '';
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editName = '';
  }

  saveEdit(company: ServiceCompanyAdmin): void {
    const name = this.validateName(this.editName, company.id);
    if (!name) return;

    this.isSaving = true;
    this.errorMessage = '';
    this.lookupService.updateServiceCompany(company.id, { name }).subscribe({
      next: updated => {
        this.companies = this.sortCompanies(this.companies.map(item => item.id === updated.id ? updated : item));
        this.cancelEdit();
        this.isSaving = false;
      },
      error: error => {
        this.isSaving = false;
        this.errorMessage = getApiErrorMessage(error, 'Unable to update the service company.');
      }
    });
  }

  deactivate(company: ServiceCompanyAdmin): void {
    const confirmRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Deactivate Service Company',
        message: `Deactivate "${company.name}"? It will no longer be available for new project selections.`,
        confirmLabel: 'Deactivate',
        cancelLabel: 'Cancel',
        variant: 'warning'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.isSaving = true;
      this.errorMessage = '';
      this.lookupService.deactivateServiceCompany(company.id).subscribe({
        next: () => {
          this.companies = this.sortCompanies(
            this.companies.map(item => item.id === company.id ? { ...item, isActive: false } : item));
          this.isSaving = false;
        },
        error: error => {
          this.isSaving = false;
          this.errorMessage = getApiErrorMessage(error, 'Unable to deactivate the service company.');
        }
      });
    });
  }

  activate(company: ServiceCompanyAdmin): void {
    this.isSaving = true;
    this.errorMessage = '';
    this.lookupService.activateServiceCompany(company.id).subscribe({
      next: activated => {
        this.companies = this.sortCompanies(
          this.companies.map(item => item.id === activated.id ? activated : item));
        this.isSaving = false;
      },
      error: error => {
        this.isSaving = false;
        this.errorMessage = getApiErrorMessage(error, 'Unable to reactivate the service company.');
      }
    });
  }

  hardDelete(company: ServiceCompanyAdmin): void {
    const confirmRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
      width: '440px',
      data: {
        title: 'Permanently Delete Service Company',
        message: `Permanently delete "${company.name}"? Existing projects assigned to this company will be changed to no service company. This action cannot be undone.`,
        confirmLabel: 'Delete Permanently',
        cancelLabel: 'Cancel',
        variant: 'warning'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.isSaving = true;
      this.errorMessage = '';
      this.lookupService.hardDeleteServiceCompany(company.id).subscribe({
        next: () => {
          this.companies = this.companies.filter(item => item.id !== company.id);
          this.isSaving = false;
        },
        error: error => {
          this.isSaving = false;
          this.errorMessage = getApiErrorMessage(error, 'Unable to permanently delete the service company.');
        }
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private validateName(value: string, currentId?: number): string | null {
    const name = value.trim();
    if (!name) {
      this.errorMessage = 'Service company name is required.';
      return null;
    }
    if (name.length > 200) {
      this.errorMessage = 'Service company name cannot exceed 200 characters.';
      return null;
    }
    const duplicate = this.companies.some(company =>
      company.id !== currentId && company.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase());
    if (duplicate) {
      this.errorMessage = 'A service company with this name already exists.';
      return null;
    }
    return name;
  }

  private sortCompanies(companies: ServiceCompanyAdmin[]): ServiceCompanyAdmin[] {
    return [...companies].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
  }
}
